import {
  ApiError,
  ERROR_CODES,
  MILLIS_PER_HOUR,
  MILLIS_PER_MINUTE,
  type EpochMillis,
} from '@luacentrix/shared';
import { SECURITY_EVENT_SEVERITIES, type SecurityEventSeverity } from '@luacentrix/contracts';

export const NOTIFICATION_KINDS = [
  'threat_detected',
  'auto_blocked',
  'auth_anomaly',
  'api_abuse_threshold',
  'config_changed',
] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_READ_STATUSES = ['unread', 'read', 'dismissed'] as const;

export type NotificationReadStatus = (typeof NOTIFICATION_READ_STATUSES)[number];

export const NOTIFICATION_DELIVERY_STATUSES = ['skipped', 'attempted', 'sent', 'failed'] as const;

export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

export const DISCORD_FAILURE_CODES = [
  'webhook_unconfigured',
  'webhook_invalid',
  'payload_too_large',
  'transport_timeout',
  'transport_error',
  'rejected_client',
  'rejected_server',
  'rejected_unreadable',
] as const;

export type DiscordFailureCode = (typeof DISCORD_FAILURE_CODES)[number];

export interface NotificationPolicy {
  enabled: boolean;
  kinds: Record<NotificationKind, boolean>;
  min_severity: SecurityEventSeverity;
  window_ms: number;
  max_per_window: number;
  discord_enabled: boolean;
}

export const MIN_NOTIFICATION_WINDOW_MS = MILLIS_PER_MINUTE;

export const MAX_NOTIFICATION_WINDOW_MS = 6 * MILLIS_PER_HOUR;

export const MAX_NOTIFICATIONS_PER_WINDOW_LIMIT = 100;

export const NOTIFICATION_TEXT_MAX_LENGTH = 200;

export const NOTIFICATION_SUMMARY_MAX_LENGTH = 320;

export const DISCORD_CONTENT_MAX_CHARS = 1800;

export const DEFAULT_NOTIFICATION_POLICY: NotificationPolicy = {
  enabled: true,
  kinds: {
    threat_detected: true,
    auto_blocked: true,
    auth_anomaly: true,
    api_abuse_threshold: true,
    config_changed: true,
  },
  min_severity: 'medium',
  window_ms: 15 * MILLIS_PER_MINUTE,
  max_per_window: 12,
  discord_enabled: false,
};

export const NOTIFICATION_TITLES: Record<NotificationKind, string> = {
  threat_detected: 'High confidence threat detected',
  auto_blocked: 'Automatic IP restriction armed',
  auth_anomaly: 'Unusual authentication pattern',
  api_abuse_threshold: 'API abuse exceeded the configured threshold',
  config_changed: 'Critical security configuration changed',
};

const SEVERITY_ORDER: Record<SecurityEventSeverity, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

export function severityAtLeast(
  severity: SecurityEventSeverity,
  minimum: SecurityEventSeverity,
): boolean {
  return SEVERITY_ORDER[severity] >= SEVERITY_ORDER[minimum];
}

export function highestOf(
  left: SecurityEventSeverity,
  right: SecurityEventSeverity,
): SecurityEventSeverity {
  return SEVERITY_ORDER[right] > SEVERITY_ORDER[left] ? right : left;
}

function fail(field: string, reason: string): never {
  throw new ApiError(ERROR_CODES.VALIDATION_FAILED, { details: { field, reason } });
}

function readBoolean(value: unknown, field: string, fallback: boolean): boolean {
  if (value === undefined) {
    return fallback;
  }
  if (typeof value !== 'boolean') {
    fail(field, 'boolean_required');
  }
  return value;
}

function readInteger(
  value: unknown,
  field: string,
  minimum: number,
  maximum: number,
  fallback: number,
): number {
  if (value === undefined || value === null) {
    return fallback;
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isSafeInteger(value)) {
    fail(field, 'integer_required');
  }
  if (value < minimum) {
    fail(field, 'number_minimum');
  }
  if (value > maximum) {
    fail(field, 'number_maximum');
  }
  return value;
}

function readSeverity(
  value: unknown,
  field: string,
  fallback: SecurityEventSeverity,
): SecurityEventSeverity {
  if (value === undefined) {
    return fallback;
  }
  if (
    typeof value !== 'string' ||
    !(SECURITY_EVENT_SEVERITIES as readonly string[]).includes(value)
  ) {
    fail(field, 'enum');
  }
  return value as SecurityEventSeverity;
}

function readKinds(value: unknown, field: string): Record<NotificationKind, boolean> {
  const output = {} as Record<NotificationKind, boolean>;
  if (value === undefined) {
    for (const kind of NOTIFICATION_KINDS) {
      output[kind] = DEFAULT_NOTIFICATION_POLICY.kinds[kind];
    }
    return output;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail(field, 'object_required');
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(NOTIFICATION_KINDS as readonly string[]).includes(key)) {
      fail(`${field}.${key}`, 'unknown_kind');
    }
  }
  for (const kind of NOTIFICATION_KINDS) {
    output[kind] = readBoolean(
      record[kind],
      `${field}.${kind}`,
      DEFAULT_NOTIFICATION_POLICY.kinds[kind],
    );
  }
  return output;
}

export function parseNotificationPolicy(input: unknown): NotificationPolicy {
  if (input === undefined || input === null) {
    return cloneNotificationPolicy(DEFAULT_NOTIFICATION_POLICY);
  }
  if (typeof input !== 'object' || Array.isArray(input)) {
    fail('notifications', 'object_required');
  }
  const record = input as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (
      key !== 'enabled' &&
      key !== 'kinds' &&
      key !== 'min_severity' &&
      key !== 'window_ms' &&
      key !== 'max_per_window' &&
      key !== 'discord_enabled'
    ) {
      fail(`notifications.${key}`, 'unknown_field');
    }
  }
  return {
    enabled: readBoolean(
      record['enabled'],
      'notifications.enabled',
      DEFAULT_NOTIFICATION_POLICY.enabled,
    ),
    kinds: readKinds(record['kinds'], 'notifications.kinds'),
    min_severity: readSeverity(
      record['min_severity'],
      'notifications.min_severity',
      DEFAULT_NOTIFICATION_POLICY.min_severity,
    ),
    window_ms: readInteger(
      record['window_ms'],
      'notifications.window_ms',
      MIN_NOTIFICATION_WINDOW_MS,
      MAX_NOTIFICATION_WINDOW_MS,
      DEFAULT_NOTIFICATION_POLICY.window_ms,
    ),
    max_per_window: readInteger(
      record['max_per_window'],
      'notifications.max_per_window',
      1,
      MAX_NOTIFICATIONS_PER_WINDOW_LIMIT,
      DEFAULT_NOTIFICATION_POLICY.max_per_window,
    ),
    discord_enabled: readBoolean(
      record['discord_enabled'],
      'notifications.discord_enabled',
      DEFAULT_NOTIFICATION_POLICY.discord_enabled,
    ),
  };
}

export function cloneNotificationPolicy(policy: NotificationPolicy): NotificationPolicy {
  return {
    enabled: policy.enabled,
    kinds: { ...policy.kinds },
    min_severity: policy.min_severity,
    window_ms: policy.window_ms,
    max_per_window: policy.max_per_window,
    discord_enabled: policy.discord_enabled,
  };
}

export function notificationEnabledFor(
  policy: NotificationPolicy,
  kind: NotificationKind,
): boolean {
  return policy.enabled && policy.kinds[kind] === true;
}

export function notificationWindowStart(policy: NotificationPolicy, now: EpochMillis): EpochMillis {
  return Math.floor(now / policy.window_ms) * policy.window_ms;
}

export interface NotificationWindow {
  dedupe_key: string;
  first_seen_at: EpochMillis;
  last_seen_at: EpochMillis;
  window_expires_at: EpochMillis;
  occurrence_count: number;
}

export interface NotificationCandidate {
  kind: NotificationKind;
  severity: SecurityEventSeverity;
  dedupe_key: string;
}

export type NotificationSuppressionReason =
  'disabled' | 'kind_disabled' | 'below_minimum_severity' | 'cap_reached';

export type NotificationPlan =
  | { readonly status: 'suppressed'; readonly reason: NotificationSuppressionReason }
  | {
      readonly status: 'aggregated';
      readonly occurrence_count: number;
      readonly last_seen_at: EpochMillis;
      readonly window_expires_at: EpochMillis;
    }
  | { readonly status: 'notify'; readonly window_expires_at: EpochMillis };

export interface PlanInput {
  readonly policy: NotificationPolicy;
  readonly candidate: NotificationCandidate;
  readonly now: EpochMillis;
  readonly open_window: NotificationWindow | null;
  readonly created_in_window: number;
}

export function planNotification(input: PlanInput): NotificationPlan {
  const { policy, candidate, now } = input;
  if (!policy.enabled) {
    return { status: 'suppressed', reason: 'disabled' };
  }
  if (policy.kinds[candidate.kind] !== true) {
    return { status: 'suppressed', reason: 'kind_disabled' };
  }
  if (!severityAtLeast(candidate.severity, policy.min_severity)) {
    return { status: 'suppressed', reason: 'below_minimum_severity' };
  }
  const window = input.open_window;
  if (window !== null && window.window_expires_at > now && window.last_seen_at <= now) {
    return {
      status: 'aggregated',
      occurrence_count: window.occurrence_count + 1,
      last_seen_at: now,
      window_expires_at: window.window_expires_at,
    };
  }
  if (input.created_in_window >= policy.max_per_window) {
    return { status: 'suppressed', reason: 'cap_reached' };
  }
  return { status: 'notify', window_expires_at: now + policy.window_ms };
}

const DEDUPE_SUBJECT_MAX_LENGTH = 96;

const DEDUPE_UNSAFE_CHARACTERS = /[^A-Za-z0-9:._/-]+/g;

export function notificationDedupeKey(kind: NotificationKind, subject: string | null): string {
  const normalized = (subject ?? 'global')
    .trim()
    .toLowerCase()
    .replace(DEDUPE_UNSAFE_CHARACTERS, '_')
    .slice(0, DEDUPE_SUBJECT_MAX_LENGTH);
  return `${kind}:${normalized === '' ? 'global' : normalized}`;
}

const IPV4_SOURCE = '\\d{1,3}(?:\\.\\d{1,3}){3}(?:/\\d{1,3})?';

const IPV6_SOURCE = '(?:[0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(?:/\\d{1,3})?';

const ADDRESS_SOURCE = `(?:${IPV4_SOURCE})|(?:${IPV6_SOURCE})`;

const ADDRESS_TOKEN = new RegExp(`(?<!\\w)(?:${ADDRESS_SOURCE})(?!\\w)`, 'g');

const URL_TOKEN = /\bhttps?:\/\/\S+/gi;

const CREDENTIAL_TOKEN =
  /\b(authorization|token|secret|password|cookie|session)\s*[:=]\s*[^\r\n,;]*/gi;

const LONG_OPAQUE_TOKEN = /\b[A-Za-z0-9+/._~%-]{40,}\b/g;

const WHITESPACE_RUN = /\s+/g;

function privacyPreservable(token: string): boolean {
  const separator = token.indexOf('/');
  if (separator < 0) {
    return false;
  }
  const prefix = Number.parseInt(token.slice(separator + 1), 10);
  if (!Number.isFinite(prefix) || prefix < 0) {
    return false;
  }
  const body = token.slice(0, separator);
  const maximum = body.includes(':') ? 64 : 24;
  return prefix <= maximum;
}

function redactAddresses(value: string): string {
  return value.replace(ADDRESS_TOKEN, match => (privacyPreservable(match) ? match : '[redacted]'));
}

export function redactUntrusted(value: string, maxLength = NOTIFICATION_TEXT_MAX_LENGTH): string {
  const withoutUrls = value.replace(URL_TOKEN, '[redacted]');
  const withoutCredentials = withoutUrls.replace(
    CREDENTIAL_TOKEN,
    (_match, key: string) => `${key}=[redacted]`,
  );
  const withoutAddresses = redactAddresses(withoutCredentials);
  const withoutOpaque = withoutAddresses.replace(LONG_OPAQUE_TOKEN, '[redacted]');
  const collapsed = withoutOpaque.replace(WHITESPACE_RUN, ' ').trim();
  if (collapsed.length <= maxLength) {
    return collapsed;
  }
  return `${collapsed.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

export interface DiscordPayload {
  readonly content: string;
  readonly bytes: number;
}

export interface NotificationContentFields {
  readonly id: string;
  readonly kind: NotificationKind;
  readonly severity: SecurityEventSeverity;
  readonly title: string;
  readonly summary: string;
  readonly subject_mask: string | null;
  readonly occurrence_count: number;
  readonly first_seen_at: EpochMillis;
  readonly last_seen_at: EpochMillis;
  readonly reference: string | null;
}

function notificationLines(fields: NotificationContentFields): string[] {
  return [
    `${fields.severity.toUpperCase()} · ${NOTIFICATION_TITLES[fields.kind]}`,
    fields.summary === '' ? null : redactUntrusted(fields.summary, NOTIFICATION_SUMMARY_MAX_LENGTH),
    fields.subject_mask === null ? null : `Network: ${redactUntrusted(fields.subject_mask)}`,
    `Occurrences: ${Math.max(1, Math.trunc(fields.occurrence_count))}`,
    `First seen: ${new Date(fields.first_seen_at).toISOString()}`,
    `Last seen: ${new Date(fields.last_seen_at).toISOString()}`,
    fields.reference === null ? null : `Reference: ${redactUntrusted(fields.reference)}`,
    `Notification: ${redactUntrusted(fields.id)}`,
  ].filter((line): line is string => line !== null && line !== '');
}

export function buildDiscordPayload(fields: NotificationContentFields): DiscordPayload | null {
  const heading = `LuaCentrix security · ${redactUntrusted(fields.title)}`;
  const body = notificationLines(fields);
  let content = [heading, ...body].join('\n');
  if (content.length > DISCORD_CONTENT_MAX_CHARS) {
    content = `${content.slice(0, DISCORD_CONTENT_MAX_CHARS - 3).trimEnd()}...`;
  }
  const bytes = new TextEncoder().encode(content).length;
  if (bytes > DISCORD_CONTENT_MAX_CHARS) {
    return null;
  }
  return { content, bytes };
}
